import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';
import { MenuComponent } from '@app/components/menu';
import { AppNavigationService } from '@app/services/app-navigation.service';
import { AppStarterService } from '@app/services/app-starter.service';
import { AppStorageService } from '@app/services/app-storage.service';
import { AppService } from '@app/services/app.service';
import { CustomMessageService } from '@app/services/custom-message.service';
import { UserService } from '@app/services/user.service';
import { translations } from '@app/utils/translations';
import {
    KuiLoadingComponent,
    KuiLoadingService,
    KuiProgressComponent,
    KuiToastComponent,
} from '@kone/ui';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet,
        FormsModule,
        MenuComponent,
        TranslateModule,
        KuiToastComponent,
        KuiLoadingComponent,
        KuiProgressComponent,
    ],
    template: `
        <kui-loading />
        <kui-toast position="top-right" />
        @if (loading()) {
            <div class="h-dvh flex flex-col justify-between items-center">
                <div class="flex-grow flex items-center justify-center">
                    <img src="assets/images/kone-logo-blue.png" width="250" alt="kone" />
                </div>
                <div class="mb-page flex flex-col items-center">
                    <div class="text-soft uppercase text-xs mb-1">
                        {{ translations.global.loading | translate }}...
                    </div>
                    <kui-progress mode="indeterminate" [width]="225" />
                </div>
            </div>
        } @else {
            <div class="wrapper" [class.logged-in]="isLoggedIn()">
                @if (
                    isLoggedIn() &&
                    !router.url.startsWith('/auth') &&
                    router.url !== '/information/nouser'
                ) {
                    <app-menu />
                }
                <router-outlet />
            </div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: `
        :host {
            display: flex;
            flex-direction: column;

            .wrapper {
                .logged-in {
                    top: var(--header-height);
                }
            }
        }
    `,
})
export class AppComponent {
    protected readonly translations = translations;

    private appStarterService = inject(AppStarterService);
    private appService = inject(AppService);
    private userService = inject(UserService);
    private appNavigationService = inject(AppNavigationService);
    private appStorageService = inject(AppStorageService);
    private toastService = inject(CustomMessageService);
    private loadingService = inject(KuiLoadingService);
    protected readonly router = inject(Router);

    loading = inject(AppService).loading;
    user = inject(UserService).user;

    isLoggedIn = computed(() => this.userService.isLoggedIn() && this.userService.user().hasRole);

    constructor() {
        this.appStarterService.initServices().then(async hasError => {
            if (hasError) {
                await this.appService.logout();
            } else {
                if (this.user().redirectToLanding && this.router.url !== '/information/nouser') {
                    this.appNavigationService.navigateTo('auth', {
                        parameter: 'landing',
                    });
                }
            }
            this.appService.stopLoading();
        });
    }
}
